import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUp, ChevronDown, Building2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => { if (!n || n === 0) return '--'; const a = Math.abs(n); const s = n < 0 ? '-' : ''; if (a >= 1e7) return `${s}${(a/1e7).toFixed(2)} Cr`; if (a >= 1e5) return `${s}${(a/1e5).toFixed(2)} L`; return `${s}₹${Math.round(a).toLocaleString('en-IN')}`; };

export default function ExpectedCashflow({ token, refDate }) {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!token) return;
    const params = {};
    if (refDate) params.ref_date = refDate;
    axios.get(`${API}/kpi/expected-cashflow`, { headers: { Authorization: `Bearer ${token}` }, params })
      .then(r => setData(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps -- API/axios are module constants, setters are stable
  }, [token, refDate]);

  if (!data || data.total_projects === 0) return null;

  const totals = data.period_totals || {};
  const labels = data.period_labels || {};
  const projects = data.projects || [];

  return (
    <Card className="bg-white rounded-2xl border-slate-200/60 shadow-sm overflow-hidden" data-testid="expected-cashflow">
      <button onClick={() => setExpanded(!expanded)} className="w-full">
        <CardHeader className="pb-3 border-b border-slate-100 bg-gradient-to-r from-[#0F1D32] to-[#1a2d4a] text-white hover:from-[#162540] transition-colors cursor-pointer">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <TrendingUp className="h-4 w-4 text-emerald-400" />
              <CardTitle className="text-sm sm:text-base font-bold" style={{ fontFamily: 'Manrope' }}>Expected Monthly Collection</CardTitle>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-4 text-[10px]">
                {Object.entries(totals).map(([k, v]) => (
                  <span key={k} className="hidden sm:inline"><span className="text-slate-400">{labels[k]}:</span> <span className="font-bold text-emerald-400">{fmt(v)}</span></span>
                ))}
              </div>
              <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
            </div>
          </div>
        </CardHeader>
      </button>
      {expanded && (
        <CardContent className="p-0">
          {/* Period totals */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-0 border-b border-slate-100">
            {Object.entries(totals).map(([k, v]) => (
              <div key={k} className="p-3 sm:p-4 border-r border-slate-100 last:border-r-0 text-center">
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">{labels[k]}</p>
                <p className="text-base sm:text-lg font-bold text-emerald-600 mt-0.5" style={{ fontFamily: 'Manrope' }}>{fmt(v)}</p>
              </div>
            ))}
          </div>
          {/* Project breakdown */}
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left py-2.5 px-3 font-semibold text-slate-600 sticky left-0 bg-slate-50 min-w-[150px]">Project</th>
                  <th className="text-right py-2.5 px-3 font-semibold text-slate-500 min-w-[90px]">Net Sales</th>
                  {Object.keys(totals).map(k => (
                    <th key={k} className="text-right py-2.5 px-3 font-semibold text-emerald-600 min-w-[90px]">{labels[k]}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {projects.map((p, i) => (
                  <tr key={i} className={`border-b border-slate-100 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
                    <td className="py-2 px-3 font-medium text-slate-800 sticky left-0 bg-inherit">
                      <div className="flex items-center gap-1.5"><Building2 className="h-3 w-3 text-slate-400" />{p.project}</div>
                    </td>
                    <td className="py-2 px-3 text-right text-slate-500">{fmt(p.net_sales)}</td>
                    <td className="py-2 px-3 text-right text-emerald-600 font-medium">{fmt(p.current_month)}</td>
                    <td className="py-2 px-3 text-right text-emerald-600 font-medium">{fmt(p.next_month)}</td>
                    <td className="py-2 px-3 text-right text-emerald-600 font-medium">{fmt(p.current_quarter)}</td>
                    <td className="py-2 px-3 text-right text-emerald-600 font-medium">{fmt(p.current_fy)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-[#0F1D32] text-white font-bold">
                  <td className="py-2.5 px-3 sticky left-0 bg-[#0F1D32]">Total</td>
                  <td className="py-2.5 px-3 text-right text-slate-400">—</td>
                  {Object.keys(totals).map(k => (
                    <td key={k} className="py-2.5 px-3 text-right text-emerald-300">{fmt(totals[k])}</td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
